import type {
  Logger as DatadogLogger,
  StatusType,
} from '@datadog/browser-logs';
import type { TraceId } from './trace-id';

//#region Types
export type DatadogLogContext = {
  messageContext?: Record<string, unknown>;
  error?: Error;
  traceId?: TraceId;
  status?: StatusType;
};

export type LoggerConfig = {
  isMonitoringEnabled: boolean;
  isMonitoringWithoutTraceIdEnabled: boolean;
  areConsoleLogsEnabled: boolean;
};
//#endregion

//#region Constants
export enum HeaderStyles {
  info = 'color: #6495ED; font-weight: bold;',
  warn = 'color: #DAA520; font-weight: bold;',
  error = 'color: #DC143C; font-weight: bold;',
  debug = 'color: #32CD32; font-weight: bold;',
}
//#endregion

//#region Utility Functions
export const prependTraceId = (message: string, traceId: TraceId): string => {
  return `[${traceId.toString()}] ${message}`;
};

export const canBeLoggedToDatadog = (
  message: string,
  logContext: DatadogLogContext,
  config: LoggerConfig
): boolean => {
  if (!config.isMonitoringEnabled) return false;
  if (
    !config.isMonitoringWithoutTraceIdEnabled &&
    !logContext.traceId &&
    !message.includes('traceId')
  )
    return false;

  return true;
};

export const getCleanPayloadArray = ({
  messageContext,
  error,
}: DatadogLogContext = {}): DatadogLogContext[] => {
  return [messageContext, error].filter(Boolean) as DatadogLogContext[];
};
//#endregion

const logHeader = (headerText: string, headerStyle: HeaderStyles): void => {
  console.info(`%c ${headerText}`, headerStyle);
};

//#region Log Level Handler
export const logLevelHandler = (
  message: string,
  logContext: DatadogLogContext,
  config: LoggerConfig
): void => {
  if (!config.areConsoleLogsEnabled) return;

  const { status } = logContext;
  const payload = getCleanPayloadArray(logContext);
  const headerText = canBeLoggedToDatadog(message, logContext, config)
    ? 'DATADOG RECEIVES '
    : '';

  switch (status) {
    case 'info':
      logHeader(headerText + 'INFO', HeaderStyles.info);
      console.info(message, ...payload);
      break;
    case 'warn':
      logHeader(headerText + 'WARN', HeaderStyles.warn);
      console.warn(message, ...payload);
      break;
    case 'error':
      logHeader(headerText + 'ERROR', HeaderStyles.error);
      console.error(message, ...payload);
      break;
    case 'debug':
      logHeader(headerText + 'DEBUG', HeaderStyles.debug);
      console.debug(message, ...payload);
      break;
  }
};

export const logToDatadog = (
  message: string,
  logContext: DatadogLogContext,
  config: LoggerConfig,
  loggerInstance: Nullable<DatadogLogger>
): void => {
  if (!canBeLoggedToDatadog(message, logContext, config)) return;

  const { messageContext, status, error } = logContext;
  (loggerInstance as DatadogLogger).log(message, messageContext, status, error);
};
//#endregion
